import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api';
import './BillingListing.css';

const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

function recompute(r) {
  const rate = Number(r.billing_rate) || 0;
  const model = (r.billing_method || '').toLowerCase();
  const hrsChange = Number(r.hrs_change) || 0;
  const billable = Math.max(0, (Number(r.worked_hours) || 0) + hrsChange);
  const days = Number(r.worked_days) || 0;
  const std = Number(r.std_units_override ?? r.std_units) || 0;
  let calc;
  if (model.includes('fixed')) calc = rate;
  else if (model.includes('standard hour')) calc = std ? (rate * billable / std) : 0;
  else if (model.includes('standard day')) calc = std ? (rate * days / std) : 0;
  else if (model.includes('day')) calc = rate * days;
  else calc = rate * billable;
  const adj = Number(r.adjustment) || 0;
  return {
    ...r,
    billable_hours: billable,
    calculated_value: Number((calc + adj).toFixed(2)),
  };
}

export default function BillingListing() {
  const { customerId } = useParams();
  const [customer, setCustomer] = useState(null);
  const [msas, setMsas] = useState([]);
  const [groups, setGroups] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [filters, setFilters] = useState({
    msa_ids: [],
    billing_group_ids: [],
    month: todayMonth(),
    billing_freq: 'Whole Month',
    from_date: '',
    to_date: '',
  });
  const [data, setData] = useState({ rows: [], window: { from: '', to: '' }, std_units: 0 });
  const [forexValue, setForexValue] = useState('');
  const [genMsg, setGenMsg] = useState('');
  const [toggles, setToggles] = useState({
    changeDates: false,
    editHours: false,
    adjustments: false,
    editStd: false,
    showForex: false,
  });

  useEffect(() => {
    api.get(`/customers/${customerId}`).then(setCustomer);
    api.get(`/billing/customer/${customerId}/msas`).then(({ msas, groups }) => { setMsas(msas); setGroups(groups); });
    api.get('/settings/hrs-change-reasons').then(setReasons).catch(() => setReasons([]));
  }, [customerId]);

  const fetchListing = useCallback(() => {
    const qs = new URLSearchParams();
    qs.set('customer_id', customerId);
    if (filters.msa_ids.length) qs.set('msa_ids', filters.msa_ids.join(','));
    if (filters.billing_group_ids.length) qs.set('billing_group_ids', filters.billing_group_ids.join(','));
    if (filters.month) qs.set('month', filters.month);
    if (filters.billing_freq) qs.set('billing_freq', filters.billing_freq);
    if (toggles.changeDates && filters.from_date) qs.set('from_date', filters.from_date);
    if (toggles.changeDates && filters.to_date) qs.set('to_date', filters.to_date);
    api.get(`/billing/listing?${qs.toString()}`).then(d => {
      const rows = (d.rows || []).map(r => recompute({
        ...r,
        hrs_change: 0,
        hrs_change_reason: '',
        adjustment: 0,
        std_units_override: null,
      }));
      setData({ ...d, rows });
    });
  }, [customerId, filters, toggles.changeDates]);

  useEffect(() => { fetchListing(); }, [fetchListing]);

  const groupsForFilter = useMemo(() => {
    if (!filters.msa_ids.length) return groups;
    const ids = new Set(filters.msa_ids);
    return groups.filter(g => ids.has(g.msa_id));
  }, [filters.msa_ids, groups]);

  const groupedMsas = msas.some(m => m.billing_mode === 'grouped' && filters.msa_ids.includes(m.msa_id));
  const billingGroupDisabled = !filters.msa_ids.length || !groupedMsas || groupsForFilter.length === 0;

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const toggle = (k) => setToggles(t => ({ ...t, [k]: !t[k] }));

  const sameCurrency = data.rows.length > 0 && data.rows.every(r => r.billing_currency === r.invoice_currency || !r.invoice_currency);
  const forexDisabled = sameCurrency;

  const listingColCount = useMemo(() => {
    let n =
      8 +
      (toggles.changeDates ? 2 : 0) +
      1 +
      (toggles.editHours ? 2 : 0) +
      (toggles.editStd ? 2 : 0) +
      1 +
      (toggles.adjustments ? 1 : 0) +
      1 +
      (toggles.showForex ? 1 : 0);
    return n;
  }, [
    toggles.changeDates,
    toggles.editHours,
    toggles.editStd,
    toggles.adjustments,
    toggles.showForex,
  ]);

  const updateRow = (sow_id, patch) => {
    setData(d => ({
      ...d,
      rows: d.rows.map(r => r.sow_id === sow_id ? recompute({ ...r, ...patch }) : r),
    }));
  };

  const computeBill = (r) => {
    if (!toggles.showForex) return null;
    const fx = Number(forexValue);
    if (!fx || isNaN(fx)) return null;
    return Number((r.calculated_value * fx).toFixed(2));
  };

  const validForGeneration = () => {
    for (const r of data.rows) {
      if ((Number(r.hrs_change) || 0) !== 0 && !r.hrs_change_reason) return `Hrs Change Reason is required for ${r.sow_id}`;
    }
    if (toggles.showForex && !forexDisabled && !forexValue) return 'Forex value required when forex toggle is on.';
    return null;
  };

  const handleGenerate = async () => {
    const err = validForGeneration();
    if (err) { setGenMsg(err); return; }
    if (!data.rows.length) { setGenMsg('No rows to invoice.'); return; }
    if (!window.confirm(`Generate ${data.rows.length} invoice(s) as Draft?`)) return;
    try {
      const res = await api.post('/billing/generate', {
        customer_id: customerId,
        period_from: data.window?.from,
        period_to: data.window?.to,
        forex_rate: Number(forexValue) || null,
        rows: data.rows.map(r => ({
          sow_id: r.sow_id,
          worked_hours: r.worked_hours,
          billable_hours: r.billable_hours,
          std_units: r.std_units_override ?? r.std_units,
          adjustment: r.adjustment || 0,
          calculated_value: r.calculated_value,
          hrs_change: r.hrs_change || 0,
          hrs_change_reason: r.hrs_change_reason || '',
        })),
      });
      setGenMsg(`Created ${res.created?.length || 0} invoice(s): ${(res.created || []).join(', ')}`);
    } catch (e) {
      setGenMsg(`Failed: ${e.message}`);
    }
  };

  return (
    <>
      <div className="page-header">
        <h1>Billing — {customer?.legal_name || customerId}</h1>
      </div>
      <div className="page-content billing-listing-page clause-library-page">
        <nav className="page-back-nav" aria-label="Back navigation">
          <Link to="/invoices" className="page-back-link">
            ← Back to Billing
          </Link>
        </nav>
        <div className="billing-listing-context" aria-label="Customer reference">
          Customer <strong>{customerId}</strong>
          {customer?.default_currency ? ` · Default currency ${customer.default_currency}` : ''}
        </div>
        <div className="billing-listing-summary" aria-label="Billing context">
          <div className="billing-listing-summary-card">
            <div className="billing-listing-summary-card-label">Billing window</div>
            <div className={`billing-listing-summary-card-value ${data.window?.from ? 'billing-listing-summary-card-value-wrap' : ''}`}>
              {data.window?.from && data.window?.to ? `${data.window.from} → ${data.window.to}` : '—'}
            </div>
          </div>
          <div className="billing-listing-summary-card">
            <div className="billing-listing-summary-card-label">Listing rows</div>
            <div className="billing-listing-summary-card-value tabular-nums">{data.rows.length.toLocaleString()}</div>
          </div>
          <div className="billing-listing-summary-card">
            <div className="billing-listing-summary-card-label">Std units (month)</div>
            <div className="billing-listing-summary-card-value tabular-nums">
              {data.std_units != null ? Number(data.std_units).toLocaleString() : '—'}
            </div>
          </div>
        </div>

        <div className="card billing-listing-filters-card">
          <div className="card-header">Filters</div>
          <div className="form-grid">
            <div className="form-group"><label>MSA (multi)</label>
              <select multiple value={filters.msa_ids} size={Math.min(4, Math.max(2, msas.length))}
                onChange={e => setF('msa_ids', Array.from(e.target.selectedOptions).map(o => o.value))}>
                {msas.map(m => <option key={m.msa_id} value={m.msa_id}>{m.msa_id}{m.msa_ref ? ` (${m.msa_ref})` : ''} · {m.billing_mode}</option>)}
              </select>
            </div>
            <div className="form-group"><label>Billing Group</label>
              <select multiple disabled={billingGroupDisabled} value={filters.billing_group_ids} size={Math.min(4, Math.max(2, groupsForFilter.length || 2))}
                onChange={e => setF('billing_group_ids', Array.from(e.target.selectedOptions).map(o => o.value))}>
                {groupsForFilter.map(g => <option key={g.id} value={g.id}>{g.group_name}</option>)}
              </select>
              {billingGroupDisabled && (
                <span className="billing-listing-filter-note">No grouped MSAs in selection.</span>
              )}
            </div>
            <div className="form-group"><label>Month of Billing</label>
              <input type="month" value={filters.month} onChange={e => setF('month', e.target.value)} />
            </div>
            <div className="form-group"><label>Billing Frequency</label>
              <select value={filters.billing_freq} onChange={e => setF('billing_freq', e.target.value)}>
                <option>Whole Month</option><option>First Half</option><option>Second Half</option>
              </select>
            </div>
            <div className="form-group"><label>From Date {toggles.changeDates ? '' : '(auto)'}</label>
              <input type="date" value={toggles.changeDates ? filters.from_date : data.window?.from || ''} disabled={!toggles.changeDates}
                onChange={e => setF('from_date', e.target.value)} />
            </div>
            <div className="form-group"><label>To Date {toggles.changeDates ? '' : '(auto)'}</label>
              <input type="date" value={toggles.changeDates ? filters.to_date : data.window?.to || ''} disabled={!toggles.changeDates}
                onChange={e => setF('to_date', e.target.value)} />
            </div>
          </div>
        </div>

        <div className="card billing-listing-listing-card">
          <div className="card-header billing-listing-toolbar-header">
            <span className="billing-listing-toolbar-title">
              Billing listing ({data.rows.length} {data.rows.length === 1 ? 'row' : 'rows'})
            </span>
            <div className="billing-listing-toolbar-actions btn-group">
              <button
                type="button"
                className={`btn btn-sm ${toggles.changeDates ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => toggle('changeDates')}
              >
                Change Billing Dates
              </button>
              <button type="button" className={`btn btn-sm ${toggles.editHours ? 'btn-primary' : 'btn-outline'}`} onClick={() => toggle('editHours')}>
                Edit Hours
              </button>
              <button
                type="button"
                className={`btn btn-sm ${toggles.adjustments ? 'btn-primary' : 'btn-outline'}`}
                onClick={() => toggle('adjustments')}
              >
                Adjustments
              </button>
              <button type="button" className={`btn btn-sm ${toggles.editStd ? 'btn-primary' : 'btn-outline'}`} onClick={() => toggle('editStd')}>
                Edit Standard Units
              </button>
              <button
                type="button"
                className={`btn btn-sm ${toggles.showForex ? 'btn-primary' : 'btn-outline'}`}
                disabled={forexDisabled}
                onClick={() => !forexDisabled && toggle('showForex')}
              >
                Show Forex Conversions
              </button>
              <button type="button" className="btn btn-sm btn-success" onClick={handleGenerate} disabled={!data.rows.length}>
                Generate Invoice(s)
              </button>
            </div>
          </div>

          {genMsg && <div className="billing-listing-alert billing-listing-alert-success">{genMsg}</div>}

          {toggles.showForex && (
            <div className="form-grid billing-listing-forex-grid">
              <div className="form-group"><label>Invoice Currency</label>
                <input value={data.rows[0]?.invoice_currency || ''} readOnly />
              </div>
              <div className="form-group"><label>Exchange Value (1 {data.rows[0]?.billing_currency || 'src'} = ?)</label>
                <input type="number" step="0.0001" value={forexValue} onChange={e => setForexValue(e.target.value)} placeholder="From currency master if blank" />
              </div>
            </div>
          )}
          {toggles.showForex && !forexValue && (
            <div className="billing-listing-alert billing-listing-alert-warning">
              Forex value needs to be fed above the table. Bill Value column will be blank until provided.
            </div>
          )}

          <div className="billing-listing-table-shell table-container">
            <table>
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Employee Name</th>
                  <th>RFP #</th>
                  <th>MSA #</th>
                  <th>SOW #</th>
                  <th>Billing Method</th>
                  <th className="billing-listing-num">Rate</th>
                  <th>Currency</th>
                  {toggles.changeDates && <th>From</th>}
                  {toggles.changeDates && <th>To</th>}
                  <th className="billing-listing-num">Worked Hours</th>
                  {toggles.editHours && <th className="billing-listing-num">Hrs Change</th>}
                  {toggles.editHours && <th>Hrs Change Reason</th>}
                  {toggles.editStd && <th>Std Measure</th>}
                  {toggles.editStd && <th className="billing-listing-num">Std Units</th>}
                  <th className="billing-listing-num">Billable Hours</th>
                  {toggles.adjustments && <th className="billing-listing-num">Adjustment</th>}
                  <th className="billing-listing-num">Calculated Value</th>
                  {toggles.showForex && <th className="billing-listing-num">Bill Value (INV)</th>}
                </tr>
              </thead>
              <tbody>
                {data.rows.map(r => {
                  const reasonRequired = (Number(r.hrs_change) || 0) !== 0;
                  const reasonMissing = reasonRequired && !r.hrs_change_reason;
                  return (
                    <tr key={r.sow_id}>
                      <td>{r.employee_id}</td>
                      <td>{r.employee_name}</td>
                      <td>{r.rfp_num}</td>
                      <td>{r.msa_id}</td>
                      <td>{r.sow_id}</td>
                      <td>{r.billing_method}</td>
                      <td className="billing-listing-num">{r.billing_rate?.toLocaleString()}</td>
                      <td>{r.billing_currency}</td>
                      {toggles.changeDates && <td>{r.from_date}</td>}
                      {toggles.changeDates && <td>{r.to_date}</td>}
                      <td className="billing-listing-num">{r.worked_hours}</td>
                      {toggles.editHours && (
                        <td className="billing-listing-num">
                          <input
                            type="number"
                            step="0.01"
                            className="billing-listing-input-xs"
                            value={r.hrs_change ?? 0}
                            onChange={e =>
                              updateRow(r.sow_id, { hrs_change: e.target.value === '' ? 0 : Number(e.target.value) })
                            }
                          />
                        </td>
                      )}
                      {toggles.editHours && (
                        <td>
                          <select
                            value={r.hrs_change_reason || ''}
                            className={reasonMissing ? 'billing-listing-reason-error' : undefined}
                            onChange={e => updateRow(r.sow_id, { hrs_change_reason: e.target.value })}
                          >
                            <option value="">— select —</option>
                            {reasons.filter(x => x.is_active).map(x => (
                              <option key={x.id} value={x.reason}>
                                {x.reason}
                              </option>
                            ))}
                          </select>
                          {reasonMissing && <div className="billing-listing-field-error">Required</div>}
                        </td>
                      )}
                      {toggles.editStd && <td>{r.std_unit_type || '—'}</td>}
                      {toggles.editStd && (
                        <td className="billing-listing-num">
                          <input
                            type="number"
                            step="0.01"
                            className="billing-listing-input-sm"
                            value={r.std_units_override ?? r.std_units ?? 0}
                            onChange={e =>
                              updateRow(r.sow_id, {
                                std_units_override: e.target.value === '' ? null : Number(e.target.value),
                              })
                            }
                          />
                        </td>
                      )}
                      <td className="billing-listing-num">{r.billable_hours}</td>
                      {toggles.adjustments && (
                        <td className="billing-listing-num">
                          <input
                            type="number"
                            step="0.01"
                            className="billing-listing-input-md"
                            value={r.adjustment ?? 0}
                            onChange={e =>
                              updateRow(r.sow_id, { adjustment: e.target.value === '' ? 0 : Number(e.target.value) })
                            }
                          />
                        </td>
                      )}
                      <td className="billing-listing-num-strong">{r.calculated_value?.toLocaleString()}</td>
                      {toggles.showForex && (
                        <td className="billing-listing-num-strong">{computeBill(r)?.toLocaleString() ?? ''}</td>
                      )}
                    </tr>
                  );
                })}
                {!data.rows.length && (
                  <tr>
                    <td colSpan={listingColCount} className="billing-listing-empty">
                      No active SOWs match the filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
